/**
 * Home ウィジェットの計算まわり。
 *
 * JSX を持たない純粋な処理だけを置く（コンポーネント定義と同居させると
 * Fast Refresh が効かなくなるため）。
 */

import { format, isToday } from 'date-fns'

import { parseUtcDate } from '@/lib/date-utils'
import { parseWidgetSize, type WidgetSize } from '@/store/useHomeLayoutStore'

export type NavigateFn = (path: string) => void

/** 当日は時刻、それ以外は M/d で期限を出す */
export const formatDeadline = (iso: string): string => {
  const date = parseUtcDate(iso)
  return isToday(date) ? format(date, 'HH:mm') : format(date, 'M/d')
}

/** 幅が 1 マスしかない。数値ひとつを縦に積む */
export const isTile = (size: WidgetSize): boolean => parseWidgetSize(size).cols === 1

/** 高さが 1 マスしかない。横一列に並べる */
export const isRow = (size: WidgetSize): boolean => {
  const { rows, cols } = parseWidgetSize(size)
  return rows === 1 && cols > 1
}

/** 補足の数値や一覧を添えられる幅があるか */
export const isWide = (size: WidgetSize): boolean => parseWidgetSize(size).cols >= 3

/** カード上部の見出しと主要な数値が占める行数 */
const HEADER_ROWS = 2

/** 3 列以上あると見出しの横が空くので、そこに入れられる行数 */
const ROWS_BESIDE_HEADER = 2

/** 見出しより下の 1 行に収まる一覧の行数 */
const ROWS_PER_GRID_ROW = 4

/** カード表示で下に並べられる一覧の行数。ヘッダーと見出しの数値ぶんを差し引いた概算 */
export const cardListRows = (size: WidgetSize): number => {
  const { rows, cols } = parseWidgetSize(size)
  if (rows < HEADER_ROWS || cols < 2) return 0
  const besideHeader = cols < 3 ? 0 : ROWS_BESIDE_HEADER
  return besideHeader + (rows - HEADER_ROWS) * ROWS_PER_GRID_ROW
}

/** 筋トレの回数を数える期間。週替わりで 0 に戻らないよう今日を含む直近 7 日で見る */
export const WORKOUT_WINDOW_DAYS = 7

export const ratioOf = (actual: number, target: number | null): number => (target && target > 0 ? actual / target : 0)

export const clampRatio = (ratio: number): number => Math.min(Math.max(ratio, 0), 1)
